import classNames from "classnames";
import React, { FC, useMemo, useState } from "react";
import { LuSearch, LuTrash2, LuUsers, LuX } from "react-icons/lu";
import { useDebounce } from "usehooks-ts";

import { getInitialsFromFullName } from "../../../constants/constants";
import { CustomerModel } from "../../../factories/customers/models/customerModel";
import { getLinkedFirm } from "../../../sessionStorage/sessionStorage";
import { useGetSearchCustomersQuery } from "../../../slices/ContactsSlice";
import { IUser } from "../../../types/types";
import { Toggle, UserAvatar, userFullName } from "./editUserParts";

export type AssignedClient = {
  id: string;
  name: string;
  isOrganization: boolean;
};

export const toAssignedClient = (customer: CustomerModel): AssignedClient => ({
  id: customer.customerId,
  name: customer.customerName,
  isOrganization: !!customer.businessContactId,
});

type Props = {
  user: IUser;
  avatarColor: string;
  allClients: AssignedClient[];
  assignedAll: boolean;
  assignedClients: AssignedClient[];
  onClose: () => void;
  onSave: (assignedAll: boolean, clients: AssignedClient[]) => void;
};

const ClientAvatar: FC<{ client: AssignedClient }> = ({ client }) => (
  <span
    className={classNames(
      "flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white",
      client.isOrganization ? "bg-[#860F86]" : "bg-[#1B3E9F]",
    )}>
    {getInitialsFromFullName(client.name).toUpperCase()}
  </span>
);

export const AssignClientsModal: FC<Props> = ({
  user,
  avatarColor,
  allClients,
  assignedAll: initialAssignedAll,
  assignedClients: initialClients,
  onClose,
  onSave,
}) => {
  const [assignedAll, setAssignedAll] = useState(initialAssignedAll);
  const [clients, setClients] = useState<AssignedClient[]>(
    initialAssignedAll ? allClients : initialClients,
  );
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search.trim(), 300);

  const { data: searchResults, isFetching } = useGetSearchCustomersQuery(
    {
      orgId: getLinkedFirm()?.orgId || "",
      searchQuery: debouncedSearch,
      userId: user.id,
      isActive: true,
    },
    { skip: !debouncedSearch },
  );

  const assignedIds = useMemo(
    () => new Set(clients.map((client) => client.id)),
    [clients],
  );
  const results = useMemo(
    () =>
      (searchResults ?? [])
        .map(toAssignedClient)
        .filter((client) => !assignedIds.has(client.id))
        .slice(0, 8),
    [searchResults, assignedIds],
  );

  const addClient = (client: AssignedClient) => {
    const next = [...clients, client];
    setClients(next);
    setAssignedAll(allClients.length > 0 && next.length >= allClients.length);
    setSearch("");
  };

  const removeClient = (id: string) => {
    setClients((current) => current.filter((client) => client.id !== id));
    setAssignedAll(false);
  };

  const toggleAll = (checked: boolean) => {
    setAssignedAll(checked);
    setClients(checked ? allClients : []);
  };

  return (
    <div className={"modal modal-open font-Manrope"}>
      <div
        role={"dialog"}
        aria-modal={"true"}
        aria-label={"Assign user to clients"}
        className={
          "modal-box flex max-h-[calc(100vh-48px)] w-[520px] max-w-[calc(100vw-32px)] flex-col overflow-hidden rounded-[14px] p-0 shadow-[0_24px_70px_rgba(0,0,0,0.28)]"
        }>
        <div
          className={
            "relative flex items-center justify-center border-b border-[#EDEFF3] px-6 py-5"
          }>
          <div className={"text-[17px] font-extrabold text-[#1F2433]"}>
            Assign user to clients
          </div>
          <button
            type={"button"}
            aria-label={"Close"}
            onClick={onClose}
            className={
              "absolute right-6 text-[#B4B9C6] transition-colors hover:text-[#1F2433]"
            }>
            <LuX size={18} />
          </button>
        </div>

        <div className={"min-h-0 flex-1 overflow-y-auto px-6 py-5"}>
          <div
            className={
              "flex items-center gap-3 rounded-xl border border-[#EDEFF3] bg-[#F8F9FB] px-4 py-3"
            }>
            <UserAvatar user={user} color={avatarColor} size={"sm"} />
            <div className={"min-w-0 flex-1"}>
              <div
                className={
                  "truncate text-[15px] font-extrabold text-[#1F2433]"
                }>
                {userFullName(user)}
              </div>
              <div className={"truncate text-[12.5px] text-[#8A90A2]"}>
                {user.userProfile?.email}
              </div>
            </div>
            <label
              className={
                "flex shrink-0 cursor-pointer items-center gap-2 text-[13px] font-semibold text-[#3A4256]"
              }>
              Assign all clients
              <Toggle
                checked={assignedAll}
                label={"Assign all clients"}
                onChange={toggleAll}
              />
            </label>
          </div>

          <div className={"mb-2 mt-6 text-sm font-bold text-[#293B5A]"}>
            Add clients
          </div>
          <div className={"relative"}>
            <LuSearch
              size={16}
              className={
                "pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-[#8A90A2]"
              }
            />
            <input
              type={"text"}
              value={search}
              placeholder={"Search clients to assign..."}
              onChange={(event) => setSearch(event.target.value)}
              className={
                "h-[43px] w-full rounded-[9px] border border-[#DCE2EA] bg-white pl-10 pr-3 text-sm text-[#1F2433] outline-none placeholder:text-[#9AA1AE] focus:border-[#6B4EFF]"
              }
            />
            {!!search.trim() && (
              <div
                className={
                  "absolute left-0 right-0 top-[calc(100%+4px)] z-10 max-h-[260px] overflow-y-auto rounded-[10px] border border-[#ECEFF3] bg-white py-1 shadow-[0_12px_32px_rgba(16,24,40,0.12)]"
                }>
                {results.map((client) => (
                  <button
                    key={client.id}
                    type={"button"}
                    onClick={() => addClient(client)}
                    className={
                      "flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-[#F6F4FF]"
                    }>
                    <ClientAvatar client={client} />
                    <span
                      className={
                        "truncate text-sm font-semibold text-[#1F2433]"
                      }>
                      {client.name}
                    </span>
                  </button>
                ))}
                {!results.length && (
                  <div
                    className={
                      "px-3 py-3 text-center text-[13px] text-[#8A90A2]"
                    }>
                    {isFetching || search.trim() !== debouncedSearch
                      ? "Searching..."
                      : `No clients match "${search.trim()}"`}
                  </div>
                )}
              </div>
            )}
          </div>

          <div
            className={
              "mb-2 mt-6 flex items-center gap-2 text-sm font-bold text-[#293B5A]"
            }>
            Clients assigned
            <span
              className={
                "rounded-full bg-[#F0EDFF] px-2 py-0.5 text-[11.5px] font-bold text-[#6B4EFF]"
              }>
              {clients.length}
            </span>
          </div>
          {clients.length ? (
            <div
              className={
                "max-h-[280px] overflow-y-auto rounded-xl border border-[#ECEFF3]"
              }>
              {clients.map((client) => (
                <div
                  key={client.id}
                  className={
                    "flex items-center gap-3 border-b border-[#F4F5F7] px-4 py-2.5 last:border-b-0"
                  }>
                  <ClientAvatar client={client} />
                  <span
                    className={
                      "min-w-0 flex-1 truncate text-sm font-semibold text-[#1F2433]"
                    }>
                    {client.name}
                  </span>
                  <button
                    type={"button"}
                    aria-label={`Remove ${client.name}`}
                    onClick={() => removeClient(client.id)}
                    className={
                      "rounded-md p-1.5 text-[#9AA1AE] transition-colors hover:bg-[#FDF3F3] hover:text-[#D14343]"
                    }>
                    <LuTrash2 size={15} />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div
              className={
                "flex flex-col items-center rounded-xl border border-dashed border-[#DCE2EA] px-6 py-8 text-center"
              }>
              <span
                className={
                  "mb-3 flex h-10 w-10 items-center justify-center rounded-[10px] bg-[#F1F2F5] text-[#8A90A2]"
                }>
                <LuUsers size={18} />
              </span>
              <div className={"text-[13px] text-[#8A90A2]"}>
                No clients assigned yet.
                <br />
                Search above or turn on{" "}
                <b className={"font-bold text-[#3A4256]"}>Assign all clients</b>
                .
              </div>
            </div>
          )}
        </div>

        <div
          className={
            "flex flex-none items-center justify-end gap-2.5 border-t border-[#EDEFF3] px-6 py-4"
          }>
          <button
            type={"button"}
            onClick={onClose}
            className={
              "h-[38px] rounded-lg border border-[#DCE2EA] bg-white px-[22px] text-sm font-bold text-[#3A4256] hover:bg-[#F8F9FB]"
            }>
            Cancel
          </button>
          <button
            type={"button"}
            onClick={() => onSave(assignedAll, clients)}
            className={
              "h-[38px] rounded-lg bg-[#6B4EFF] px-6 text-sm font-bold text-white shadow-[0_4px_14px_rgba(107,78,255,0.28)] hover:bg-[#5A3EEB]"
            }>
            Update
          </button>
        </div>
      </div>
    </div>
  );
};
